import React, { useState, useEffect } from 'react';
import moment from 'moment';
import ReactMarkdown from 'react-markdown';
import { DatabaseEvent, SafeDatabaseUser } from '../../../types/types';
import CountdownTimer from '../countdownTimer';
import { getUsers } from '../../../services/userService';
import { shareEvent } from '../../../services/eventService';
import useUserContext from '../../../hooks/useUserContext';
import './index.css';

/**
 * EventDetails component displays the details of an event.
 *
 * @event - The event object containing event details.
 * @onClose - Function to close the event details.
 * @onEdit - Function to edit the event.
 * @onDelete - Function to delete the event.
 */
interface EventDetailsProps {
  event: DatabaseEvent;
  onClose: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

/**
 * EventDetails component displays detailed information about a selected event.
 * Includes support for markdown descriptions, countdown for major events,
 * and sharing private events with other users.
 *
 * @param event The event to display.
 * @param onClose Function to close the details panel.
 * @param onEdit Function to initiate event editing.
 * @param onDelete Function to delete the event.
 */
const EventDetails: React.FC<EventDetailsProps> = ({ event, onClose, onEdit, onDelete }) => {
  const [users, setUsers] = useState<SafeDatabaseUser[]>([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [shareMessage, setShareMessage] = useState<string | null>(null);
  const [, setError] = useState<string>('');
  const { user: currentUser } = useUserContext();

  /**
   * Loads a list of users that can be invited to the private event.
   * Only runs if the event is private and the current user is the creator.
   */
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await getUsers();
        const filtered = res.filter(
          u =>
            u.username !== currentUser.username && !event.userParticipating?.includes(u.username),
        );
        setUsers(filtered);
      } catch (err) {
        setError('Error fetching users');
      }
    };

    if (event.visibility === 'private' && event.userCreated === currentUser.username) {
      fetchUsers();
    }
  }, [event, currentUser.username]);

  return (
    <div className='event-details'>
      <h3>{event.eventTitle}</h3>
      <p>
        <strong>Start:</strong> {moment(event.timeStart).format('MMMM D, YYYY h:mm A')}
      </p>
      <p>
        <strong>End:</strong> {moment(event.timeEnd).format('MMMM D, YYYY h:mm A')}
      </p>
      <p>
        <strong>Created By:</strong> {event.userCreated}
      </p>
      {event.eventDescription && (
        <div className='description-block'>
          <strong>Description:</strong>
          <ReactMarkdown>{event.eventDescription || ''}</ReactMarkdown>
        </div>
      )}
      {event.isMajor && (
        <div>
          <h4>⏰ Countdown to Event:</h4>
          <CountdownTimer targetDate={new Date(event.timeStart)} />
        </div>
      )}

      {event.visibility === 'private' && event.userCreated === currentUser.username && (
        <div style={{ marginTop: '1rem' }}>
          <select value={selectedUser} onChange={e => setSelectedUser(e.target.value)}>
            <option value=''>Select a user</option>
            {users.map(user => (
              <option key={user.username} value={user.username}>
                {user.username}
              </option>
            ))}
          </select>
          <button
            onClick={async () => {
              if (!selectedUser) return;
              try {
                await shareEvent(event._id.toString(), selectedUser);
                setSelectedUser('');
                setShareMessage(`Shared with ${selectedUser}` || '');
                setTimeout(() => setShareMessage(null), 3000);
              } catch (err) {
                setError('Error sharing event');
                setTimeout(() => setError(''), 3000);
              }
            }}
            disabled={!selectedUser}
            style={{ marginLeft: '0.5rem' }}>
            Share
          </button>
        </div>
      )}
      {shareMessage && <p className='share-message'>{shareMessage}</p>}

      <div className='event-action-buttons'>
        <button onClick={onEdit}>Edit Event</button>
        <button onClick={onDelete} className='delete-button'>
          Delete Event
        </button>
        <button onClick={onClose} className='close-button'>
          Close
        </button>
      </div>
    </div>
  );
};

export default EventDetails;
